/* eslint-disable camelcase */
import {
  HStack,
  Spinner,
  useColorModeValue,
  Text,
  Button,
} from '@chakra-ui/react';
import React from 'react';

import { colors } from 'packages/common-frontend/colors';
import { Icon as FaIcon } from 'packages/common-frontend/components/icons';
import { Sanitized } from 'packages/dataPipeline/hooks/UploadContext';
import { UploadState } from 'packages/dataPipeline/hooks/useInputNode';

const IconOrSpinner = ({ icon, color }: { icon: string; color: string }) => {
  if (icon === 'spinner') {
    return <Spinner color={color} />;
  }

  return <FaIcon color={color} icon={icon} size="16px" />;
};

// includes the status of the upload and the sanitization.
type OverallUploadStatus =
  | {
      type: 'not_started';
      progressLabel: string;
    }
  | {
      type: 'success';
      progressLabel: string;
    }
  | {
      type: 'error';
      progressLabel: string;
    }
  | {
      type: 'in_progress';
      progressLabel: string;
    };

const getOverallUploadStatus = (
  status: UploadState,
  sanitized: Sanitized,
): OverallUploadStatus => {
  if (status === 'client_initial') {
    return { type: 'not_started', progressLabel: '' };
  }

  // We will include following steps + sanitizationNodeCount as total steps for an upload.
  // These do not contain any error states.
  const uploadSequence: UploadState[] = [
    'client_started',
    'client_uploaded',
    'initializing',
    'inProgress',
    'success',
  ];

  // total steps
  const total = uploadSequence.length + sanitized.total;

  // for client error. we will show only 1 step as complete.
  if (status === 'client_error') {
    return {
      type: 'error',
      progressLabel: 'Upload failed',
    };
  }

  // for server error. we will show client states as complete
  if (status === 'error') {
    const complete = uploadSequence.length - 1;
    return {
      type: 'error',
      progressLabel: `${complete} of ${total} steps complete`,
    };
  }

  if (sanitized.failure > 0) {
    const complete = uploadSequence.length + sanitized.success;
    return {
      type: 'error',
      progressLabel: `${complete} of ${total} steps complete`,
    };
  }

  if (status === 'success' && sanitized.success === sanitized.total) {
    return {
      type: 'success',
      progressLabel: `Upload complete`,
    };
  }

  const complete = uploadSequence.indexOf(status) + sanitized.success;
  return {
    type: 'in_progress',
    progressLabel: `${complete} of ${total} steps complete`,
  };
};

export const UploadStatusIndicator = ({
  status,
  sanitized,
  onClick,
  canRetry,
}: {
  status: UploadState;
  sanitized: Sanitized;
  // null while the input can't take an upload yet: the buttons are disabled.
  onClick: (() => void) | null;
  // a failed upload that the server never claimed, so it can be sent again.
  canRetry: boolean;
}) => {
  const uploadColor = useColorModeValue(colors.blue[400], colors.white[700]);
  const successColor = useColorModeValue(colors.green[400], colors.green[300]);
  const errorColor = useColorModeValue(colors.red[400], colors.red[300]);
  const overallStatus = getOverallUploadStatus(status, sanitized);

  const overallStatusStyles = {
    success: {
      textColor: successColor,
      icon: 'check-circle',
    },
    error: {
      textColor: errorColor,
      icon: 'circle-xmark',
    },
    in_progress: {
      textColor: uploadColor,
      icon: 'spinner',
    },
  } as const;

  if (overallStatus.type === 'not_started') {
    return (
      <HStack>
        <Button
          size="md"
          colorScheme="gray"
          variant="secondary"
          isDisabled={!onClick}
          onClick={onClick ?? undefined}
        >
          Upload
        </Button>
        <Text>{overallStatus.progressLabel}</Text>
      </HStack>
    );
  }

  const statusStyles = overallStatusStyles[overallStatus.type];

  if (status === 'client_error') {
    return (
      <HStack>
        <IconOrSpinner
          icon={statusStyles.icon}
          color={statusStyles.textColor}
        />
        {canRetry ? (
          <>
            <Text>{overallStatus.progressLabel}</Text>
            <Button
              size="md"
              colorScheme="gray"
              variant="secondary"
              isDisabled={!onClick}
              onClick={onClick ?? undefined}
            >
              Try again
            </Button>
          </>
        ) : (
          // the server claimed this input for the version before the file
          // failed, so the version takes no other file for it.
          <Text>{overallStatus.progressLabel}: upload a new version</Text>
        )}
      </HStack>
    );
  }

  // we want to show the icon and the label
  // there could be two icons one for upload and one for sanitization status.
  // sanitize icon will be shown only if snippet is sanitized is not not_required .
  return (
    <HStack>
      <IconOrSpinner icon={statusStyles.icon} color={statusStyles.textColor} />
      <Text>{overallStatus.progressLabel}</Text>
    </HStack>
  );
};
